import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

// Buckhead / Atlanta luxury skyline aerial — free Unsplash license
const BG_URL =
  "https://images.unsplash.com/photo-1575917649705-5b59aaa12e6b?fm=jpg&q=85&w=2400&auto=format&fit=crop&ixlib=rb-4.1.0";

// Secondary image (used behind mobile hero band) — modern Atlanta skyline aerial
const BG_URL_SECONDARY =
  "https://images.unsplash.com/photo-1663601460253-aba72eea6edf?fm=jpg&q=85&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav("/");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:flex relative flex-col justify-between p-12 border-r border-neutral-200"
        style={{
          backgroundImage: `linear-gradient(rgba(10,10,10,0.45), rgba(10,10,10,0.70)), url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div data-testid="login-core-logo-top">
          <img
            src="/brand/core-logo-white.svg"
            alt="CORE Realty and Associates"
            className="h-16 w-auto"
            data-testid="login-core-logo-image"
          />
        </div>
        <div className="text-white max-w-md">
          <div className="font-mono text-xs uppercase tracking-[0.3em] opacity-80 mb-8">Ledger / 02</div>
          <h1 className="font-serif text-5xl leading-tight">A financial ledger for the modern real estate agent.</h1>
          <p className="mt-6 font-sans text-sm opacity-80">Closed transactions, broker splits, deductible expenses, and an IRS-ready year-end report — purpose-built for CORE Realty &amp; Associates agents.</p>
          <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">Serving Metro Atlanta · Buckhead · Sandy Springs</div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-24 py-12 bg-white relative">
        {/* Mobile hero band with Atlanta skyline */}
        <div
          className="lg:hidden -mx-8 sm:-mx-16 mb-8 h-40 flex items-end p-6"
          style={{
            backgroundImage: `linear-gradient(rgba(10,10,10,0.45), rgba(10,10,10,0.70)), url(${BG_URL_SECONDARY})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid="login-mobile-hero"
        >
          <img
            src="/brand/core-logo-white.svg"
            alt="CORE Realty and Associates"
            className="h-12 w-auto"
            data-testid="login-core-logo-mobile"
          />
        </div>

        <div className="max-w-md w-full">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Agent Sign In</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-neutral-900 mb-2">Welcome back.</h2>
          <p className="text-sm text-neutral-500 mb-10">Access your deal pipeline and tax reports.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="password" className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-2"
                  data-testid="login-forgot-password-link"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-11 font-sans tracking-wide"
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
              <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-neutral-200 text-sm text-neutral-500">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-neutral-900 underline underline-offset-4" data-testid="login-go-register-link">
              Create one
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
